import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import axiosInstance from '../utils/axiosInstance';
import config from '../utils/config';
import { useAuth } from '../context/AuthContext';
import { FaTrash, FaFilter, FaCalendar, FaUser } from 'react-icons/fa';
import DatePicker from './DatePicker';
import 'react-datepicker/dist/react-datepicker.css';
import NoteImage from './NoteImage';
import NoteImageModal from './NoteImageModal';
import Button from './ui/Button';
import { Textarea } from './pilot-ui/textarea';
import { Label } from './pilot-ui/label';
import { Checkbox } from './pilot-ui/checkbox';
import { Popover, PopoverContent, PopoverTitle, PopoverTrigger } from './pilot-ui/popover';
import { Device, Note, NoteUploadURLResponse } from '../types';
import { formatTimestamp } from '../utils/formatTimestamp';
import { noteAuthor } from '../utils/noteAuthor';
import { removeRowWithReturnFocus } from './returnFocus';

interface DeviceNotesPanelProps {
  /** Resolved device whose notes this panel manages. The panel has no selector;
   *  the workspace tab owns device selection. */
  device: Device;
}

// Matches MAX_IMAGES_PER_NOTE in device-data-api's notesController.
const MAX_IMAGES_PER_NOTE = 10;

interface PendingImage {
  file: File;
  preview: string;
  /** True when the browser can't render this file in an <img> (e.g. HEIC on
   *  Chrome/Firefox). The file may still be a valid image, but we block the
   *  attach — anyone viewing the note in the same browser would see nothing. */
  previewFailed?: boolean;
}

interface ModalState {
  noteId: number;
  imageIds: number[];
  index: number;
}

/**
 * Plan 94 Phase 2 - route-bound Device Notes body.
 *
 * Extracted from the old DeviceNotes page so it can render against an explicit
 * device with no page-local DeviceSelector or cross-page pin. The workspace
 * tab owns device selection.
 */
const DeviceNotesPanel: React.FC<DeviceNotesPanelProps> = ({ device }) => {
  const deviceId = device.id;
  const [notes, setNotes] = useState<Note[]>([]);
  const [newNoteText, setNewNoteText] = useState('');
  const [filterChanged, setFilterChanged] = useState(false);
  const [selectedDateTime, setSelectedDateTime] = useState<Date | null>(new Date());
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [actionError, setActionError] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [pendingImages, setPendingImages] = useState<PendingImage[]>([]);
  const { id: userId, hasPermission } = useAuth();
  const canWrite = hasPermission('write_notes');
  const [modal, setModal] = useState<ModalState | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Revoke every outstanding preview object URL on unmount. Kept in a ref so
  // the cleanup sees the latest list without re-running per state change.
  const pendingImagesRef = useRef<PendingImage[]>([]);
  pendingImagesRef.current = pendingImages;
  useEffect(() => {
    return () => {
      pendingImagesRef.current.forEach((p) => URL.revokeObjectURL(p.preview));
    };
  }, []);

  useEffect(() => {
    // False once the panel is gone. An operator leaves the device before the
    // notes come back often enough that it is ordinary, and the state this
    // would then write lands on a component React has thrown away.
    let active = true;
    const fetchNotes = async () => {
      try {
        setLoading(true);
        setNotes([]);
        setLoadError(false);
        const response = await axiosInstance.get<Note[]>(`${config.notesApiUrl}/notes/device/${deviceId}`);
        if (!active) return;
        setNotes(response.data);
        setLoadError(false);
      } catch (error) {
        // Logged either way: a request that failed after the operator left
        // still failed, and swallowing it hides a broken notes API.
        console.error('Error fetching notes:', error);
        if (active) setLoadError(true);
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchNotes();
    return () => {
      active = false;
    };
  }, [deviceId]);

  const addFiles = useCallback((files: FileList | null | undefined) => {
    if (!files) return;
    const images = Array.from(files).filter((f) => f.type.startsWith('image/'));
    if (images.length === 0) return;
    setPendingImages((prev) => {
      const room = MAX_IMAGES_PER_NOTE - prev.length;
      return [
        ...prev,
        ...images.slice(0, room).map((file) => ({
          file,
          preview: URL.createObjectURL(file),
        })),
      ];
    });
  }, []);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    addFiles(e.dataTransfer.files);
  }, [addFiles]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(e.target.files);
    // Reset so picking the same file again re-fires onChange. Without this,
    // remove-then-reattach of the same image silently showed no preview.
    e.target.value = '';
  };

  const handleSelectFileClick = () => {
    fileInputRef.current?.click();
  };

  const removePendingImage = useCallback((index: number) => {
    setActionError('');
    setPendingImages((prev) => {
      const removed = prev[index];
      if (removed) URL.revokeObjectURL(removed.preview);
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const clearPendingImages = useCallback(() => {
    setPendingImages((prev) => {
      prev.forEach((p) => URL.revokeObjectURL(p.preview));
      return [];
    });
  }, []);

  const markPreviewFailed = useCallback((index: number) => {
    setPendingImages((prev) =>
      prev.map((p, i) => (i === index ? { ...p, previewFailed: true } : p)),
    );
  }, []);

  const uploadBlocked = pendingImages.some((p) => p.previewFailed);
  const noteTextMissing = newNoteText.trim() === '';
  const addNoteReason = noteTextMissing
    ? 'Write a note to enable Add Note.'
    : loading ? 'Wait for the current note request to finish.' : null;

  const handleAddNote = async () => {
    if (newNoteText.trim() === '') {
      setActionError('Write a note before adding it.');
      return;
    }
    if (uploadBlocked) {
      setActionError('Remove or convert attachments this browser cannot display.');
      return;
    }

    try {
      setActionError('');
      setLoading(true);

      // Image bytes go straight to S3 via presigned PUTs — never through
      // CloudFront/WAF (the WAF SizeRestrictions_BODY rule 403s any >8 KB
      // body, which blocked the old multipart upload). The note POST below
      // then carries only the small JSON + image_urls strings.
      const imageUrls: string[] = [];
      for (const pending of pendingImages) {
        const { data: uploadInfo } = await axiosInstance.post<NoteUploadURLResponse>(
          `${config.notesApiUrl}/notes/upload-url`,
          { content_type: pending.file.type, original_filename: pending.file.name }
        );
        await axios.put(uploadInfo.presigned_url, pending.file, {
          headers: { 'Content-Type': uploadInfo.content_type },
        });
        imageUrls.push(uploadInfo.image_url);
      }

      const response = await axiosInstance.post<Note>(
        `${config.notesApiUrl}/notes`,
        {
          device_id: deviceId,
          user_id: userId,
          note_text: newNoteText.trim(),
          filter_changed: filterChanged,
          created_at: selectedDateTime?.toISOString() || new Date().toISOString(),
          ...(imageUrls.length > 0 ? { image_urls: imageUrls } : {}),
        }
      );
      setNotes([...notes, response.data].sort((b, a) => a.created_at.localeCompare(b.created_at)))
      setNewNoteText('');
      setFilterChanged(false);
      setSelectedDateTime(new Date());
      clearPendingImages();
    } catch (error) {
      console.error('Error adding note:', error);
      setActionError('Could not add this note. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteNote = async (noteId: number, source: HTMLElement) => {
    try {
      setLoading(true);
      setActionError('');
      await axiosInstance.delete(`${config.notesApiUrl}/notes/${noteId}`);
      removeRowWithReturnFocus(source, () => {
        setNotes((current) => current.filter((note) => note.id !== noteId));
      });
    } catch (error) {
      console.error('Error deleting note:', error);
      setActionError('Could not delete this note. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const openImageModal = (note: Note, index: number) => {
    const imageIds = (note.images ?? []).map((img) => img.id);
    if (imageIds.length === 0) return;
    setModal({ noteId: note.id, imageIds, index });
  };

  return (
    <div>
      {canWrite && (
        <div className="mb-6">
          <Label htmlFor="new-note-text" className="mb-2">Add Note</Label>
          <div
            className={`space-y-4 ${dragActive ? 'border-2 border-dashed border-status-info-border' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <Textarea
              id="new-note-text"
              aria-describedby="new-note-help"
              value={newNoteText}
              onChange={(event) => {
                setNewNoteText(event.target.value);
                setActionError('');
              }}
              className="w-full"
              rows={4}
              placeholder="Write your note here..."
            />
            <p id="new-note-help" className="text-sm text-muted-foreground">Record what happened to this device. The note and any photos become part of its history.</p>

            <div className="flex items-start gap-2 pl-2">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept="image/*"
                multiple
                className="hidden"
              />
              <Button
                variant="secondary"
                onClick={handleSelectFileClick}
                type="button"
                disabled={pendingImages.length >= MAX_IMAGES_PER_NOTE}
                title={`Pick photos from this computer to attach to the note (up to ${MAX_IMAGES_PER_NOTE}); they upload when you add the note`}
              >
                Select Images
              </Button>
              {pendingImages.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {pendingImages.map((pending, index) => (
                    <div key={pending.preview} className="relative w-32">
                      {pending.previewFailed ? (
                        <div className="w-full h-24 rounded-md border border-status-error-border bg-red-50 flex items-center justify-center p-1">
                          <span
                            className="text-xs text-status-error-foreground text-center break-all"
                            title="This browser can't display this image format — convert it (e.g. HEIC → JPEG) and re-attach"
                          >
                            no preview: {pending.file.name}
                          </span>
                        </div>
                      ) : (
                        <img
                          src={pending.preview}
                          alt={`Preview ${index + 1}`}
                          className="w-full h-auto rounded-md"
                          onError={() => markPreviewFailed(index)}
                        />
                      )}
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => removePendingImage(index)}
                        className="absolute top-1 right-1 rounded-full"
                        type="button"
                        aria-label={`Remove image ${index + 1}`}
                        title="Take this photo off the note before it is added; nothing has been uploaded yet"
                      >
                        <FaTrash size={12} />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {uploadBlocked && (
              <p className="text-sm text-status-error-foreground">
                One or more attached files can't be displayed by this browser
                (HEIC?). Remove them or convert to JPEG/PNG before adding the note.
              </p>
            )}

            <div className="flex flex-col space-y-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="note-filter-changed"
                  checked={filterChanged}
                  onCheckedChange={setFilterChanged}
                  aria-describedby="note-filter-changed-help"
                />
                <Label htmlFor="note-filter-changed">Filter Changed</Label>
              </div>
              <p id="note-filter-changed-help" className="text-xs text-muted-foreground">Mark this note as a filter replacement in the device timeline.</p>
              <label className="flex items-center space-x-2 text-sm">
                <FaCalendar className="text-muted-foreground" />
                <span>Note date and time</span>
                <DatePicker
                  selected={selectedDateTime}
                  onChange={(date: Date | null) => setSelectedDateTime(date)}
                  ariaLabel="Note date and time"
                  ariaDescribedBy="note-date-time-help"
                  showTimeSelect
                  dateFormat="yyyy-MM-dd HH:mm:ss"
                  className="border rounded-md p-2"
                />
              </label>
              <p id="note-date-time-help" className="text-xs text-muted-foreground">Choose when the event happened; clearing the date uses the current time when you add the note.</p>
            </div>
            <div>
              {actionError && <p role="alert" className="mb-2 text-sm text-destructive">{actionError}</p>}
              <Button
                variant="primary"
                onClick={handleAddNote}
                disabled={loading || noteTextMissing}
                aria-describedby={addNoteReason ? 'add-note-reason' : undefined}
                title="Save this note, with its date and any photos, to the device's history"
              >
                Add Note
              </Button>
              {addNoteReason && <p id="add-note-reason" className="mt-1 text-sm text-muted-foreground">{addNoteReason}</p>}
            </div>
          </div>
        </div>
      )}

      {notes.length > 0 && (
        <div>
          <h2 className="text-xl font-semibold mb-4">Notes for Device</h2>
          <ul className="space-y-4">
            {notes.map((note) => (
              <li key={note.id} className="border p-4 rounded-md flex justify-between items-start">
                <div className="min-w-0 grow">
                  <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600 mb-1">
                    <span>{formatTimestamp(note.created_at)}</span>
                    <span className="flex min-w-0 items-center break-all text-muted-foreground">
                      <FaUser className="mr-1 shrink-0" />
                      {noteAuthor(note)}
                    </span>
                    {note.filter_changed && (
                      <span className="flex items-center text-foreground">
                        <FaFilter className="mr-1" /> Filter Changed
                      </span>
                    )}
                  </div>
                  <p className="whitespace-pre-wrap">{note.note_text}</p>
                  {note.images && note.images.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {note.images.map((image, index) => (
                        <Button key={image.id} variant="secondary" onClick={() => openImageModal(note, index)} title={`Open attachment ${index + 1} from this note in the image viewer`} aria-label={`Open note attachment ${index + 1}`}>
                          <NoteImage noteId={note.id} imageId={image.id} alt={`Note attachment ${index + 1}`} className="max-h-48 w-auto rounded-md" />
                        </Button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="ml-4 flex shrink-0 items-start gap-2">
                  <Popover>
                    <PopoverTrigger render={<Button variant="secondary" size="sm" title="Show this note's details and attachment actions" />}>
                      Details
                    </PopoverTrigger>
                    <PopoverContent align="end" className="max-h-[var(--available-height)] max-w-sm overflow-y-auto overscroll-contain">
                      <PopoverTitle>Note from {noteAuthor(note)}</PopoverTitle>
                      <p className="text-xs text-muted-foreground">{formatTimestamp(note.created_at)}</p>
                      <p className="whitespace-pre-wrap break-words">{note.note_text}</p>
                      {note.images && note.images.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {note.images.map((image, index) => (
                            <Button key={image.id} variant="secondary" size="sm" onClick={() => openImageModal(note, index)} title={`Open attachment ${index + 1} from this note`}>
                              Open image {index + 1}
                            </Button>
                          ))}
                        </div>
                      )}
                    </PopoverContent>
                  </Popover>
                  {canWrite && (
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={(event) => handleDeleteNote(note.id, event.currentTarget)}
                      className="ml-4"
                      title="Delete this note from the device's history for good"
                    >
                      <FaTrash />
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {loadError && !loading && <p role="alert" className="text-destructive">Could not load notes for this device.</p>}

      {notes.length === 0 && !loading && !loadError && (
        <p className="text-muted-foreground">No notes for this device.</p>
      )}

      {loading && <p className="text-muted-foreground">Loading...</p>}

      {modal != null && (
        <NoteImageModal
          noteId={modal.noteId}
          imageIds={modal.imageIds}
          initialIndex={modal.index}
          alt="Expanded view"
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
};

export default DeviceNotesPanel;
